import { theme } from 'antd';
import { groupPermissionsBySourceForPerson } from 'library/helpers/accessControl';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAssetGroups } from 'reduxStore/assetGroups/selectors';
import { selectPermissionManageableDevices } from 'reduxStore/devices/selectors';
import { selectPermissionsByPersonID } from 'reduxStore/permissions/selectors';
import { selectTeamsByPersonID } from 'reduxStore/teams/selectors';
import { Loader } from 'zComponents/atoms';
import DeviceDrawer from 'zComponents/molecules/DeviceDrawer';
import DeviceGroupsDrawer from 'zComponents/molecules/DeviceGroupDrawer';
import { DevicesIcon } from 'assets/images/svg';
import { Empty, Flex, Popover, Tag, Typography } from 'zui/Antd';

const { Text } = Typography;

const PermissionsPanelContent = ({ personID }) => {
	const {
		token: { fontWeightStrong, paddingSM, paddingXXS, colorTextDescription }
	} = theme.useToken();

	// Redux selectors
	const permissions = useSelector((state) => selectPermissionsByPersonID(state, personID));
	const devices = useSelector(selectPermissionManageableDevices);
	const deviceGroups = useSelector(selectAssetGroups);
	const teams = useSelector((state) => selectTeamsByPersonID(state, personID));

	const { loadingAddPermission, loadingDeletePermission } = useSelector((state) => state.Permissions);

	// Drawer states
	const [openDeviceDrawer, setOpenDeviceDrawer] = useState(false);
	const [selectedDeviceID, setSelectedDeviceID] = useState(null);
	const [openGroupDrawer, setOpenGroupDrawer] = useState(false);
	const [selectedGroupID, setSelectedGroupID] = useState(null);

	const displayItems = groupPermissionsBySourceForPerson({
		permissions,
		devices,
		deviceGroups,
		teams
	});

	const handleTagClick = (item) => {
		if (item.assetGroupID) {
			setSelectedGroupID(item.assetGroupID);
			setOpenGroupDrawer(true);
		} else if (item.deviceID) {
			setSelectedDeviceID(item.deviceID);
			setOpenDeviceDrawer(true);
		}
	};

	return (
		<>
			<Flex wrap gap={paddingSM} style={{ width: '100%' }}>
				{loadingAddPermission || loadingDeletePermission ? (
					<Flex vertical align='center' gap={paddingSM} style={{ width: '100%', padding: paddingSM }}>
						<Loader />
						<Text type='secondary' style={{ fontSize: 12, textAlign: 'center' }}>
							Propagating permissions to devices — this may take a moment.
							<br />
							We'll notify you when it's done.
						</Text>
					</Flex>
				) : displayItems.length === 0 ? (
					<div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
						<Empty description='No access permissions' image={Empty.PRESENTED_IMAGE_SIMPLE} />
					</div>
				) : (
					displayItems.map((item) => (
						<Popover
							key={item.permissionID}
							trigger='hover'
							placement='bottom'
							title={item.tooltip}
							content={
								item.deviceNames?.length > 0 ? (
									<Flex vertical gap={4} style={{ maxWidth: 250, maxHeight: 363, overflowY: 'auto' }}>
										{item.deviceNames.map((name, i) => (
											<Flex key={i} align='center' gap={6}>
												<DevicesIcon color={colorTextDescription} />
												<Text style={{ fontSize: 12 }}>{name}</Text>
											</Flex>
										))}
									</Flex>
								) : null
							}
						>
							<Tag
								color={item.badgeColor}
								style={{
									fontWeight: fontWeightStrong,
									borderColor: 'currentColor',
									marginInlineEnd: 0,
									cursor: 'pointer'
								}}
								onClick={() => handleTagClick(item)}
							>
								<Flex align='center' gap={paddingXXS}>
									<item.Icon color='currentColor' />
									{item.label}
								</Flex>
							</Tag>
						</Popover>
					))
				)}
			</Flex>

			<DeviceDrawer deviceID={selectedDeviceID} visible={openDeviceDrawer} setVisible={setOpenDeviceDrawer} />
			<DeviceGroupsDrawer groupID={selectedGroupID} visible={openGroupDrawer} setVisible={setOpenGroupDrawer} />
		</>
	);
};

export default PermissionsPanelContent;
